import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { User, Bot, RefreshCw, Trophy, Settings } from "lucide-react";
import GameOptions from "./GameOptions";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

const HexGame = ({ onBack }) => {
  const navigate = useNavigate();

  // Configuration
  const [boardSize, setBoardSize] = useState(7); // Default 7x7
  
  // Game State
  // Flattened array for simplicity or Matrix? Matrix is easier for logic.
  // board[row][col]
  const [board, setBoard] = useState([]);
  
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1); // 1 = Blue (Top-Bottom), 2 = Red (Left-Right)
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  // Settings
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  const initGame = (size) => {
      setBoard(Array(size).fill(null).map(() => Array(size).fill(0)));
      setBoardSize(size);
      setCurrentPlayer(1);
      setGameOver(false);
      setWinner(null);
      setGameStarted(true);
  };

  // Hex Win Logic: BFS/DFS
  // Player 1 (Blue): Top Row (row=0) to Bottom Row (row=size-1)
  // Player 2 (Red): Left Col (col=0) to Right Col (col=size-1)
  
  // Hex neighbor offsets (axial coordinates typical for rhombus board mapped to 2D array)
  // Neighbors of (r, c): 
  // (r-1, c), (r-1, c+1)  [Top-Left, Top-Right]
  // (r, c-1), (r, c+1)    [Left, Right]
  // (r+1, c-1), (r+1, c)  [Bottom-Left, Bottom-Right]
  const getNeighbors = (r, c, size) => {
      const offsets = [
          [-1, 0], [-1, 1],
          [0, -1], [0, 1],
          [1, -1], [1, 0]
      ];
      const neighbors = [];
      offsets.forEach(([dr, dc]) => {
          const nr = r + dr;
          const nc = c + dc;
          if (nr >= 0 && nr < size && nc >= 0 && nc < size) {
              neighbors.push({ r: nr, c: nc });
          }
      });
      return neighbors;
  };

  const checkWin = (currentBoard, player) => {
      const size = currentBoard.length;
      const queue = [];
      const visited = new Set();
      
      // Initialize BFS queue with all nodes on the starting side
      if (player === 1) { // Blue: Top Row
          for (let c = 0; c < size; c++) {
              if (currentBoard[0][c] === 1) {
                  queue.push({ r: 0, c });
                  visited.add(`0,${c}`);
              }
          }
      } else { // Red: Left Col
          for (let r = 0; r < size; r++) {
              if (currentBoard[r][0] === 2) {
                  queue.push({ r, 0: 0 }); // c is 0
                  visited.add(`${r},0`);
              }
          }
      }

      while (queue.length > 0) {
          const { r, c } = queue.shift();

          // Check Win Condition
          if (player === 1 && r === size - 1) return true; // Reached bottom
          if (player === 2 && c === size - 1) return true; // Reached right

          const neighbors = getNeighbors(r, c, size);
          for (const n of neighbors) {
              if (currentBoard[n.r][n.c] === player && !visited.has(`${n.r},${n.c}`)) {
                  visited.add(`${n.r},${n.c}`);
                  queue.push(n);
              }
          }
      }

      return false;
  };

  const handleMove = useCallback((r, c) => {
      return (currentBoard, currentTurn) => {
          if (currentBoard[r][c] !== 0) return null;

          const newBoard = currentBoard.map(row => [...row]);
          newBoard[r][c] = currentTurn;
          
          let weHaveAWinner = false;
          if (checkWin(newBoard, currentTurn)) {
              weHaveAWinner = true;
          }

          return {
              newBoard,
              gameOver: weHaveAWinner,
              winner: weHaveAWinner ? currentTurn : null,
              nextTurn: currentTurn === 1 ? 2 : 1
          };
      };
  }, []);

  const executeMove = (r, c) => {
      if (gameOver || !gameStarted) return;
      
      const logic = handleMove(r, c);
      const result = logic(board, currentPlayer);
      
      if (!result) return;

      setBoard(result.newBoard);
      if (result.gameOver) {
          setGameOver(true);
          setWinner(result.winner);
      } else {
          setCurrentPlayer(result.nextTurn);
      }
  };

  // AI Logic
  const getBotMove = useCallback((diff, gameState) => {
      const { board, currentPlayer } = gameState;
      const size = board.length;
      
      const availableMoves = [];
      for(let r=0; r<size; r++) {
          for(let c=0; c<size; c++) {
              if(board[r][c] === 0) availableMoves.push({ r, c });
          }
      }

      if (availableMoves.length === 0) return null;

      // Easy: Random
      if (diff === "Easy") {
          return availableMoves[Math.floor(Math.random() * availableMoves.length)];
      }

      // Medium: Try to block valid path or extend own?
      // Simple heuristic: Prefer center if available, otherwise random
      // Or: Find a move that neighbors an existing friendly stone
      if (diff !== "Easy") {
           // Basic Center control
           const center = Math.floor(size / 2);
           if (board[center][center] === 0) return { r: center, c: center };
           
           // Heuristic: Pick a neighbor to longest friendly chain? 
           // For now, random is acceptable as starter AI, implementing full Hex AI is complex (Monte Carlo).
           // Let's add a simple "Blocking" check? (Too expensive for simple JS without localized checks)
           
           // Fallback to random for now to ensure speed
           return availableMoves[Math.floor(Math.random() * availableMoves.length)];
      }

      return availableMoves[Math.floor(Math.random() * availableMoves.length)];

  }, []);

  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: ({r, c}) => executeMove(r, c),
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { board, currentPlayer },
    difficulty
  });


  // Rendering Logic (SVG)
  const hexRadius = 18; 
  const xOffset = 30;
  const yOffset = 30;

  // Calculate precise board dimensions
  const maxR = boardSize - 1;
  const maxC = boardSize - 1;
  
  // Calculate max coordinates based on the layout logic below
  // x = xOffset + (c * 35) + (r * 17.5)
  const maxX = xOffset + (maxC * 35) + (maxR * 17.5);
  const maxY = yOffset + (maxR * 30);
  
  const svgWidth = maxX + hexRadius + 10;
  const svgHeight = maxY + hexRadius + 10;

  const getGridCoordinates = (r, c) => {
      const x = xOffset + (c * 35) + (r * 17.5);
      const y = yOffset + (r * 30);
      return { x, y };
  };

  const Hexagon = ({ x, y, r, c, owner, onClick, isClickable }) => {
      const points = [];
      for (let i = 0; i < 6; i++) {
          const angle = (i * 60 + 30) * (Math.PI / 180); // Pointy top? flat top? +30 makes pointy top vertically
          points.push(`${x + hexRadius * Math.cos(angle)},${y + hexRadius * Math.sin(angle)}`);
      }
      
      const fillColor = owner === 0 ? "#f3f4f6" : (owner === 1 ? "#3b82f6" : "#ef4444");
      const strokeColor = "#9ca3af";
      
      return (
          <polygon
              points={points.join(" ")}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth="2"
              className={`transition-all duration-200 ${isClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
              onClick={onClick}
          />
      );
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Hex</h1>
        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
             Red connects Left and Right. Blue connects Top and Bottom.
             <span className="block text-sm text-gray-500 mt-1">First to create an unbroken chain wins! (No Draws!)</span>
        </p>

        {!gameStarted ? (
           <div className="text-center animate-fade-in">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={() => initGame(boardSize)}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             
             <div className="mb-6 max-w-lg mx-auto border-t pt-6">
                <label className="block text-lg font-medium text-gray-700 mb-2">Board Size:</label>
                <div className="flex justify-center space-x-4">
                     {[5, 7, 11].map(size => (
                         <button
                            key={size}
                            onClick={() => setBoardSize(size)}
                            className={`px-4 py-2 rounded-lg border-2 ${
                                boardSize === size ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white border-gray-300'
                            }`}
                         >
                             {size} x {size}
                         </button>
                     ))}
                </div>
             </div>

             <div className="mt-8 text-gray-600 max-w-2xl mx-auto text-left">
               <h3 className="font-semibold mb-4 text-lg">Detailed Rules:</h3>
               <ul className="list-disc pl-5 space-y-2 bg-gray-50 p-4 rounded-lg">
                   <li><strong>Blue</strong> tries to form a path from the top edge to the bottom edge.</li>
                   <li><strong>Red</strong> tries to form a path from the left edge to the right edge.</li>
                   <li>Players take turns placing one stone on any empty hex.</li>
                   <li>Stones cannot be moved or removed once placed.</li>
                   <li>A draw is mathematically impossible!</li>
               </ul>
             </div>
           </div>
        ) : (
           <div className="animate-fade-in">
               
               {/* HUD */}
               <div className="flex justify-between items-center mb-8 px-4 md:px-12">
                    <div className={`
                        flex flex-col items-center px-6 py-4 rounded-xl border-b-4 transition-all duration-300
                        ${currentPlayer === 1 ? 'border-blue-500 bg-blue-50 shadow-md scale-105' : 'border-transparent opacity-75'}
                    `}>
                        <div className="text-blue-800 font-bold text-lg mb-1 flex items-center gap-2">
                             <User className="w-5 h-5" /> {getPlayerName(1)}
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Top - Bottom</div>
                    </div>
                    
                    <div className="text-center">
                         {gameOver ? (
                             <div className="animate-bounce-in">
                                 <Trophy className={`w-12 h-12 mx-auto mb-2 ${winner===1?'text-blue-500':'text-red-500'}`} />
                                 <div className={`text-2xl font-bold ${winner===1?'text-blue-600':'text-red-600'}`}>
                                     {getPlayerName(winner)} Wins!
                                 </div>
                             </div>
                         ) : (
                            <div className="text-gray-400 font-bold text-2xl">VS</div>
                         )}
                         {isBotThinking && !gameOver && <div className="text-purple-500 animate-pulse text-sm mt-2">Thinking...</div>}
                    </div>

                    <div className={`
                        flex flex-col items-center px-6 py-4 rounded-xl border-b-4 transition-all duration-300
                        ${currentPlayer === 2 ? 'border-red-500 bg-red-50 shadow-md scale-105' : 'border-transparent opacity-75'}
                    `}>
                        <div className="text-red-800 font-bold text-lg mb-1 flex items-center gap-2">
                             {gameMode === 'PvC' ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />}
                             {getPlayerName(2)}
                        </div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-red-600">Left - Right</div>
                    </div>
               </div>

               {/* Board Visualization */}
               <div className="flex justify-center w-full">
                   <div className="relative w-full max-w-3xl p-4 md:p-6 bg-gray-50 rounded-xl shadow-inner border border-gray-200">
                        {/* Border Indicators */}
                        {/* Top/Bottom Blue Strokes, Left/Right Red Strokes - Simple CSS borders on the container or specialized SVG elements? 
                            Let's rely on the HUD text for now, or add colored markers.
                        */}
                        <svg 
                            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                            className="w-full h-auto max-w-full"
                        >
                            {/* Background Elements to indicate connectivity direction could go here */}
                            
                            {board.map((row, r) => (
                                row.map((_, c) => {
                                    const { x, y } = getGridCoordinates(r, c);
                                    const owner = board[r][c];
                                    const isClickable = !gameOver && !isBotThinking && owner === 0;
                                    
                                    return (
                                        <Hexagon
                                            key={`${r}-${c}`}
                                            x={x}
                                            y={y}
                                            r={r}
                                            c={c}
                                            owner={owner}
                                            isClickable={isClickable}
                                            onClick={() => isClickable && executeMove(r, c)}
                                        />
                                    );
                                })
                            ))}
                        </svg>
                   </div>
               </div>
               
               <div className="text-center text-sm text-gray-400 mb-6 italic">
                   {boardSize < 7 ? "Quick Match" : (boardSize > 10 ? "Classic Strategy" : "Standard Game")}
               </div>

               <div className="flex justify-center">
                    <button 
                        onClick={() => setGameStarted(false)} 
                        className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-full hover:bg-gray-300 transition-colors shadow-sm"
                    >
                        <RefreshCw className="w-5 h-5" />
                        New Game
                    </button>
               </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default HexGame;
